import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useState } from 'react';
import {
  boreholes, lithology, housingTypes,
  type BoreholeInput, type BoreholeHousingInput, type BoreholeLayerInput,
  type LithologyType, type BoreholeHousingType,
} from '@/api/endpoints';
import type { Borehole, BoreholeLayer, BoreholeOperatData } from '@/types';
import { Icon } from './Icon';
import { HatchingSwatch, patternStyle } from './HatchingFill';
import { CoordsInput } from './CoordsInput';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ArchiveFilesCard } from '@/components/ArchiveFilesCard';
import { collidesWith, normKey } from '@/lib/duplicates';

/** Pusta sekcja obudowy — wszystkie pola null. Wysyłana w PUT czyści całą obudowę. */
const EMPTY_HOUSING: BoreholeHousingInput = {
  typeId: null,
  diameterMm: null,
  depth: null,
  cover: null,
  steelLadderDiameterMm: null,
  hydraulicFittings: null,
};

/** Pusta sekcja operat (filtr/pompa/pomiar/test/hydrogeo) — wszystkie pola null. */
const EMPTY_OPERAT: BoreholeOperatData = {
  filterFromDepth: null, filterToDepth: null, filterType: null, filterDiameterMm: null,
  pumpType: null, pumpModel: null, pumpInstallDepth: null,
  pumpDeliveryPipeDiameterMm: null, pumpMaxCapacityM3h: null,
  meterType: null, meterModel: null, meterAccuracy: null, meterLocation: null,
  meterLastCalibration: null, meterNextCalibration: null,
  pumpingTestQM3h: null, pumpingTestDrawdownM: null,
  pumpingTestSpecificCapacity: null, pumpingTestDurationH: null,
  pumpingTestDate: null, permissibleDrawdownM: null,
  permeabilityKMs: null, transmissivityTM2s: null,
};

/**
 * Modal do edycji otworu i jego profilu geologicznego.
 * Tryby: 'create' (nowy otwór dla ujęcia) lub 'edit' (istniejący otwór + warstwy).
 */
interface Props {
  open: boolean;
  intakeId?: number;            // wymagane dla mode='create'
  borehole?: Borehole | null;   // wymagane dla mode='edit'
  /** Oznaczenia pozostałych otworów tego ujęcia — do wyłapania kolizji zanim poleci żądanie.
   *  Bez edytowanego otworu, żeby edycja innych jego pól nie potykała się o własne oznaczenie. */
  existingDesignations?: string[];
  onClose: () => void;
  onSaved: () => void;
}

const FUNCTIONS = [
  { value: 'PRODUCTION', label: 'Eksploatacyjny (E)' },
  { value: 'OBSERVATION', label: 'Obserwacyjny / piezometr (P)' },
  { value: 'REPLACEMENT', label: 'Zastępczy (Z)' },
  { value: 'INACTIVE', label: 'Nieczynny (N)' },
];

const STATUSES = [
  { value: 'ACTIVE', label: 'Czynny' },
  { value: 'INACTIVE', label: 'Nieczynny' },
  { value: 'DECOMMISSIONED', label: 'Zlikwidowany' },
];


export function BoreholeProfileModal({ open, intakeId, borehole, existingDesignations = [], onClose, onSaved }: Props) {
  const isEdit = borehole != null;
  const [form, setForm] = useState<BoreholeInput>({
    designation: '',
    function: 'PRODUCTION',
    status: 'ACTIVE',
  });
  const [layers, setLayers] = useState<BoreholeLayer[]>([]);
  const [lithologyTypes, setLithologyTypes] = useState<LithologyType[]>([]);
  const [housingTypeOptions, setHousingTypeOptions] = useState<BoreholeHousingType[]>([]);
  const [housing, setHousing] = useState<BoreholeHousingInput>(EMPTY_HOUSING);
  const [operat, setOperat] = useState<BoreholeOperatData>(EMPTY_OPERAT);
  const [operatOpen, setOperatOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Kolizja oznaczenia w obrębie ujęcia — sprawdzana na liście, którą karta ujęcia i tak ma w ręku.
  // Tak samo jak serwer: dopóki oznaczenie się nie zmienia, nie ma czego sprawdzać. Inaczej otwór,
  // który JEST zastanym duplikatem, byłby nie do zapisania — a serwer taki zapis przyjmuje.
  const designationTaken = normKey(form.designation) !== normKey(borehole?.designation)
    && collidesWith(form.designation, existingDesignations);

  useEffect(() => {
    if (!open) return;
    lithology.list().then(setLithologyTypes).catch(() => { /* nonfatal */ });
    housingTypes.list().then(setHousingTypeOptions).catch(() => { /* nonfatal */ });
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    resetDirty();
    setErr(null);
    if (borehole) {
      setForm({
        designation: borehole.designation,
        function: borehole.function as string,
        status: borehole.status as string,
        totalDepth: borehole.totalDepth,
        diameterMm: borehole.diameterMm,
        yieldM3h: borehole.yieldM3h,
        latitude: borehole.latitude,
        longitude: borehole.longitude,
        yearCompleted: borehole.yearCompleted,
        avgDailyIntakeM3: borehole.avgDailyIntakeM3,
        remarks: borehole.remarks,
      });
      setLayers([...(borehole.layers ?? [])].sort((a, b) => a.fromDepth - b.fromDepth));
      setHousing(borehole.housing ? {
        typeId: borehole.housing.typeId,
        diameterMm: borehole.housing.diameterMm,
        depth: borehole.housing.depth,
        cover: borehole.housing.cover,
        steelLadderDiameterMm: borehole.housing.steelLadderDiameterMm,
        hydraulicFittings: borehole.housing.hydraulicFittings,
      } : EMPTY_HOUSING);
      setOperat({ ...EMPTY_OPERAT, ...(borehole.operat ?? {}) });
    } else {
      setForm({ designation: '', function: 'PRODUCTION', status: 'ACTIVE' });
      setLayers([]);
      setHousing(EMPTY_HOUSING);
      setOperat(EMPTY_OPERAT);
    }
  }, [open, borehole]);

  const { requestClose, confirmation, dialogProps, resetDirty, acceptChanges } = useUnsavedChanges({
    value: { form, layers, housing, operat }, onClose, open, busy,
  });

  if (!open) return null;

  const saveBorehole = async () => {
    if (!form.designation || !form.designation.trim()) {
      setErr('Oznaczenie otworu jest wymagane (np. H-01)');
      return;
    }
    if (designationTaken) {
      setErr(`To ujęcie ma już otwór o oznaczeniu „${form.designation.trim()}". Oznaczenia muszą się różnić.`);
      return;
    }
    setBusy(true); setErr(null);
    try {
      const payload: BoreholeInput = { ...form, housing, operat };
      let boreholeId: number;
      if (isEdit && borehole) {
        await boreholes.update(borehole.id, payload);
        boreholeId = borehole.id;
      } else if (intakeId) {
        const created = await boreholes.create(intakeId, payload) as { id: number };
        boreholeId = created.id;
      } else {
        throw new Error('Brak intakeId lub otworu do edycji');
      }
      // Upsert wszystkich warstw — niezależnie od trybu.
      // Negatywne id = nowo dodane (createLayer), pozytywne = istniejące (updateLayer).
      // Każdy klik "Zapisz" persistuje aktualny stan wszystkich warstw widocznych w modalu.
      for (const w of layers) {
        if (w.id < 0) {
          await boreholes.createLayer(boreholeId, layerToInput(w));
        } else {
          await boreholes.updateLayer(w.id, layerToInput(w));
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onAddLayer = () => {
    const lastTo = layers.length > 0 ? layers[layers.length - 1].toDepth : 0;
    const def = lithologyTypes[0];
    const newLayer: BoreholeLayer = {
      id: -Date.now(), // tymczasowe ujemne id (odróżnia od zapisanych)
      fromDepth: lastTo,
      toDepth: lastTo + 5,
      symbol: def?.code ?? '',
      description: def?.name ?? '',
      color: def?.color ?? '#a8806a',
      pattern: def?.pattern ?? 'SOLID',
      lithologyTypeId: def?.id ?? null,
      lithologyTypeCode: def?.code ?? null,
      lithologyTypeName: def?.name ?? null,
    };
    setLayers([...layers, newLayer]);
  };

  /** Po wyborze typu z dropdownu — uzupełnij symbol/opis/kolor/pattern. */
  const onPickLithology = (id: number | null, layerId: number) => {
    if (id == null) {
      onUpdateLayer(layerId, { lithologyTypeId: null, lithologyTypeCode: null, lithologyTypeName: null });
      return;
    }
    const lt = lithologyTypes.find(t => t.id === id);
    if (!lt) return;
    onUpdateLayer(layerId, {
      lithologyTypeId: lt.id,
      lithologyTypeCode: lt.code,
      lithologyTypeName: lt.name,
      symbol: lt.code,
      description: lt.name,
      color: lt.color,
      pattern: lt.pattern,
    });
  };

  const onUpdateLayer = (id: number, patch: Partial<BoreholeLayer>) => {
    setLayers(layers.map(w => w.id === id ? { ...w, ...patch } : w));
  };

  const onSaveLayer = async (w: BoreholeLayer) => {
    if (!isEdit || !borehole) return; // Dla nowego otworu zapisujemy razem z otworem
    setBusy(true); setErr(null);
    try {
      let savedLayer = w;
      if (w.id < 0) {
        const saved = await boreholes.createLayer(borehole.id, layerToInput(w)) as { id: number };
        savedLayer = { ...w, id: saved.id };
        // Podmień tymczasowe id na realne
        setLayers(layers.map(x => x.id === w.id ? { ...w, id: saved.id } : x));
      } else {
        await boreholes.updateLayer(w.id, layerToInput(w));
      }
      acceptChanges(saved => ({
        ...saved,
        layers: layers.flatMap(layer => layer.id === w.id
          ? [savedLayer] : saved.layers.filter(original => original.id === layer.id)),
      }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDeleteLayer = async (w: BoreholeLayer) => {
    if (w.id < 0) {
      // Nigdy nie zapisana — tylko z lokalnego stanu
      setLayers(layers.filter(x => x.id !== w.id));
      return;
    }
    if (!confirm(`Usunąć warstwę „${w.symbol} — ${w.description}"?`)) return;
    setBusy(true); setErr(null);
    try {
      await boreholes.deleteLayer(w.id);
      acceptChanges(saved => ({ ...saved, layers: saved.layers.filter(layer => layer.id !== w.id) }));
      setLayers(layers.filter(x => x.id !== w.id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const maxDepth = Math.max(form.totalDepth ?? 0, ...layers.map(w => w.toDepth));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
         onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[1000px] w-full mt-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <Icon name="droplet" className="w-4 h-4 text-water-600"/>
          <h2 className="text-[16px] font-semibold m-0">
            {isEdit ? `Edycja otworu ${borehole?.designation}` : 'Nowy otwór wiertniczy'}
          </h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5 space-y-5 max-h-[80vh] overflow-y-auto">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}

          {/* Dane otworu */}
          <section>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-2">Dane otworu</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Oznaczenie *">
                <input value={form.designation}
                       onChange={e => setForm({ ...form, designation: e.target.value })}
                       placeholder="np. H-01"
                       className={'input' + (designationTaken ? ' !border-red-400 bg-red-50' : '')}/>
                {designationTaken && (
                  <span className="mt-1 block text-[11px] text-red-600">
                    Otwór o tym oznaczeniu już jest w tym ujęciu.
                  </span>
                )}
              </Field>
              <Field label="Funkcja">
                <SearchableSelect value={form.function}
                        onChange={v => setForm({ ...form, function: v })}>
                  {FUNCTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </SearchableSelect>
              </Field>
              <Field label="Status">
                <SearchableSelect value={form.status ?? 'ACTIVE'}
                        onChange={v => setForm({ ...form, status: v })}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </SearchableSelect>
              </Field>
              <Field label="Głębokość całkowita (m)">
                <input type="number" step="0.01" value={form.totalDepth ?? ''}
                       onChange={e => setForm({ ...form, totalDepth: e.target.value ? Number(e.target.value) : undefined })}
                       className="input font-mono"/>
              </Field>
              <Field label="Średnica (mm)">
                <input type="number" value={form.diameterMm ?? ''}
                       onChange={e => setForm({ ...form, diameterMm: e.target.value ? Number(e.target.value) : undefined })}
                       className="input font-mono"/>
              </Field>
              <Field label="Wydajność Q (m³/h)">
                <input type="number" step="0.01" value={form.yieldM3h ?? ''}
                       onChange={e => setForm({ ...form, yieldM3h: e.target.value ? Number(e.target.value) : undefined })}
                       className="input font-mono"/>
              </Field>
              <div className="col-span-3 grid grid-cols-2 gap-3">
                <CoordsInput
                  latitude={form.latitude}
                  longitude={form.longitude}
                  onChange={(lat, lng) => setForm({
                    ...form,
                    latitude: lat ?? undefined,
                    longitude: lng ?? undefined,
                  })}
                  layout="stacked"
                  label="Współrzędne otworu"
                />
              </div>
              <Field label="Rok wykonania">
                <input type="number" value={form.yearCompleted ?? ''}
                       onChange={e => setForm({ ...form, yearCompleted: e.target.value ? Number(e.target.value) : undefined })}
                       className="input font-mono"/>
              </Field>
              <Field label="Średnio-dobowy pobór (m³/dobę)">
                <input type="number" step="0.01" value={form.avgDailyIntakeM3 ?? ''}
                       onChange={e => setForm({ ...form, avgDailyIntakeM3: e.target.value ? Number(e.target.value) : null })}
                       placeholder="np. 1500"
                       title="Suma ze wszystkich studni ujęcia ×365 = roczny pobór (próg np. 1.000.000 m³/rok dla oceny w-p)"
                       className="input font-mono"/>
              </Field>
            </div>
          </section>

          {/* Uwagi + załączniki referencyjne (zdjęcia, szkice terenu) */}
          <section>
            <Field label="Uwagi">
              <textarea value={form.remarks ?? ''}
                        onChange={e => setForm({ ...form, remarks: e.target.value || undefined })}
                        placeholder="Uwagi terenowe, obserwacje, warunki wykonania otworu…"
                        className="input min-h-[52px]"/>
            </Field>
            {isEdit && borehole && (
              <div className="mt-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1">
                  Załączniki referencyjne (zdjęcia, szkice terenu)
                </div>
                <ArchiveFilesCard scope="borehole" scopeId={borehole.id} scopeName={borehole.designation}/>
              </div>
            )}
          </section>

          {/* Obudowa studni */}
          <section>
            <div className="flex items-center mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Obudowa studni</div>
              <span className="ml-2 text-[11px] text-ink-400">opcjonalne — wszystkie pola</span>
              <button
                type="button"
                onClick={() => setHousing(EMPTY_HOUSING)}
                disabled={busy}
                className="ml-auto btn btn-secondary btn-sm"
                title="Wyczyść wszystkie pola obudowy"
              >
                <Icon name="x" className="w-3.5 h-3.5"/> Wyczyść
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Typ">
                <SearchableSelect
                  value={housing.typeId == null ? '' : String(housing.typeId)}
                  onChange={v => setHousing({ ...housing, typeId: v ? Number(v) : null })}
                  title="Typ obudowy z katalogu (Administracja → Słowniki opracowań → Typy obudów studni)"
                >
                  <option value="">— nie określono —</option>
                  {housingTypeOptions.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </SearchableSelect>
              </Field>
              <Field label="Średnica (mm)">
                <input type="number" value={housing.diameterMm ?? ''}
                       onChange={e => setHousing({ ...housing, diameterMm: e.target.value ? Number(e.target.value) : null })}
                       className="input font-mono"/>
              </Field>
              <Field label="Głębokość (m)">
                <input type="number" step="0.01" value={housing.depth ?? ''}
                       onChange={e => setHousing({ ...housing, depth: e.target.value ? Number(e.target.value) : null })}
                       className="input font-mono"/>
              </Field>
              <Field label="Pokrywa">
                <input value={housing.cover ?? ''}
                       onChange={e => setHousing({ ...housing, cover: e.target.value || null })}
                       placeholder="np. Aluminiowa Ø600"
                       className="input"/>
              </Field>
              <Field label="Złaz stalowy — średnica (mm)">
                <input type="number" value={housing.steelLadderDiameterMm ?? ''}
                       onChange={e => setHousing({ ...housing, steelLadderDiameterMm: e.target.value ? Number(e.target.value) : null })}
                       className="input font-mono"/>
              </Field>
              <Field label="Armatura hydrauliczna">
                <textarea
                  value={housing.hydraulicFittings ?? ''}
                  onChange={e => setHousing({ ...housing, hydraulicFittings: e.target.value || null })}
                  placeholder="np. Zasuwa kołnierzowa DN80, manometr 0–6 bar, przepływomierz"
                  className="input min-h-[34px]"
                  rows={2}
                />
              </Field>
            </div>
          </section>

          {/* Dane do operatu wodnoprawnego (Faza 3) — składana sekcja */}
          <section>
            <div className="flex items-center mb-2">
              <button type="button" onClick={() => setOperatOpen(o => !o)}
                      className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                <Icon name="chevron" className={'w-3 h-3 transition-transform ' + (operatOpen ? 'rotate-90' : '')}/>
                Filtra / pompa / pomiar / próbne pompowanie / hydrogeologia
              </button>
              <span className="ml-2 text-[11px] text-ink-400">do operatu wodnoprawnego</span>
              {operatOpen && (
                <button type="button" onClick={() => setOperat(EMPTY_OPERAT)} disabled={busy}
                        className="ml-auto btn btn-secondary btn-sm" title="Wyczyść wszystkie pola">
                  <Icon name="x" className="w-3.5 h-3.5"/> Wyczyść
                </button>
              )}
            </div>
            {operatOpen && (
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] font-semibold text-ink-500 mb-1">Filtra</div>
                  <div className="grid grid-cols-4 gap-2">
                    <Field label="Filtr od (m)">
                      <input type="number" step="0.01" value={operat.filterFromDepth ?? ''}
                             onChange={e => setOperat({ ...operat, filterFromDepth: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Filtr do (m)">
                      <input type="number" step="0.01" value={operat.filterToDepth ?? ''}
                             onChange={e => setOperat({ ...operat, filterToDepth: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Typ filtra">
                      <input value={operat.filterType ?? ''}
                             onChange={e => setOperat({ ...operat, filterType: e.target.value || null })}
                             placeholder="np. filtr siatkowy stalowy" className="input"/>
                    </Field>
                    <Field label="Średnica filtra (mm)">
                      <input type="number" value={operat.filterDiameterMm ?? ''}
                             onChange={e => setOperat({ ...operat, filterDiameterMm: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-ink-500 mb-1">Pompa głębinowa</div>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Typ pompy">
                      <input value={operat.pumpType ?? ''}
                             onChange={e => setOperat({ ...operat, pumpType: e.target.value || null })}
                             placeholder="np. głębinowa wielostopniowa" className="input"/>
                    </Field>
                    <Field label="Model">
                      <input value={operat.pumpModel ?? ''}
                             onChange={e => setOperat({ ...operat, pumpModel: e.target.value || null })}
                             placeholder="np. Grundfos SP 30-7" className="input"/>
                    </Field>
                    <Field label="Głębokość montażu (m)">
                      <input type="number" step="0.01" value={operat.pumpInstallDepth ?? ''}
                             onChange={e => setOperat({ ...operat, pumpInstallDepth: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Średnica rurociągu tłocznego (mm)">
                      <input type="number" value={operat.pumpDeliveryPipeDiameterMm ?? ''}
                             onChange={e => setOperat({ ...operat, pumpDeliveryPipeDiameterMm: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Max wydajność (m³/h)">
                      <input type="number" step="0.01" value={operat.pumpMaxCapacityM3h ?? ''}
                             onChange={e => setOperat({ ...operat, pumpMaxCapacityM3h: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-ink-500 mb-1">Urządzenie pomiarowe (wodomierz)</div>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Typ">
                      <input value={operat.meterType ?? ''}
                             onChange={e => setOperat({ ...operat, meterType: e.target.value || null })}
                             placeholder="np. wodomierz magnetoindukcyjny" className="input"/>
                    </Field>
                    <Field label="Model">
                      <input value={operat.meterModel ?? ''}
                             onChange={e => setOperat({ ...operat, meterModel: e.target.value || null })}
                             className="input"/>
                    </Field>
                    <Field label="Dokładność">
                      <input value={operat.meterAccuracy ?? ''}
                             onChange={e => setOperat({ ...operat, meterAccuracy: e.target.value || null })}
                             placeholder="np. klasa B, ±1%" className="input"/>
                    </Field>
                    <Field label="Lokalizacja">
                      <input value={operat.meterLocation ?? ''}
                             onChange={e => setOperat({ ...operat, meterLocation: e.target.value || null })}
                             placeholder="np. kabina filtracyjna, rurociąg DN80" className="input"/>
                    </Field>
                    <Field label="Ostatnia legalizacja">
                      <input type="date" value={operat.meterLastCalibration ?? ''}
                             onChange={e => setOperat({ ...operat, meterLastCalibration: e.target.value || null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Następna legalizacja">
                      <input type="date" value={operat.meterNextCalibration ?? ''}
                             onChange={e => setOperat({ ...operat, meterNextCalibration: e.target.value || null })}
                             className="input font-mono"/>
                    </Field>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-ink-500 mb-1">Próbne pompowanie</div>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Q (m³/h)">
                      <input type="number" step="0.01" value={operat.pumpingTestQM3h ?? ''}
                             onChange={e => setOperat({ ...operat, pumpingTestQM3h: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Depresja s (m)">
                      <input type="number" step="0.01" value={operat.pumpingTestDrawdownM ?? ''}
                             onChange={e => setOperat({ ...operat, pumpingTestDrawdownM: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="q = Q/s (m³/h/m)">
                      <input type="number" step="0.0001" value={operat.pumpingTestSpecificCapacity ?? ''}
                             onChange={e => setOperat({ ...operat, pumpingTestSpecificCapacity: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Czas (h)">
                      <input type="number" value={operat.pumpingTestDurationH ?? ''}
                             onChange={e => setOperat({ ...operat, pumpingTestDurationH: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Data">
                      <input type="date" value={operat.pumpingTestDate ?? ''}
                             onChange={e => setOperat({ ...operat, pumpingTestDate: e.target.value || null })}
                             className="input font-mono"/>
                    </Field>
                    <Field label="Depresja dopuszczalna (m)">
                      <input type="number" step="0.01" value={operat.permissibleDrawdownM ?? ''}
                             onChange={e => setOperat({ ...operat, permissibleDrawdownM: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-ink-500 mb-1">Hydrogeologia (do Sichardta R=575·s·√k)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Współczynnik filtracji k (m/s)">
                      <input type="number" step="0.000000001" value={operat.permeabilityKMs ?? ''}
                             onChange={e => setOperat({ ...operat, permeabilityKMs: e.target.value ? Number(e.target.value) : null })}
                             placeholder="np. 0.00015" className="input font-mono"/>
                    </Field>
                    <Field label="Przewodność hydrauliczna T (m²/s)">
                      <input type="number" step="0.000000001" value={operat.transmissivityTM2s ?? ''}
                             onChange={e => setOperat({ ...operat, transmissivityTM2s: e.target.value ? Number(e.target.value) : null })}
                             className="input font-mono"/>
                    </Field>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Profil geologiczny */}
          <section>
            <div className="flex items-center mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Profil geologiczny</div>
            </div>

            {layers.length === 0 ? (
              <div className="text-ink-400 text-[12px] py-4 italic">
                Brak warstw. Kliknij <strong>Dodaj warstwę</strong>, aby zacząć budować profil.
              </div>
            ) : (
              <div className="grid grid-cols-[80px_1fr] gap-3">
                {/* Pasek wizualny ze szrafurą */}
                <div className="flex flex-col" style={{ height: 360 }}>
                  {layers.map(w => {
                    const h = maxDepth > 0 ? ((w.toDepth - w.fromDepth) / maxDepth) * 100 : 100 / layers.length;
                    return (
                      <div key={w.id}
                           className="border-b border-white/40 flex items-center justify-center text-[10px] font-mono font-bold"
                           style={{
                             height: h + '%',
                             color: 'rgba(0,0,0,0.7)',
                             textShadow: '0 0 3px rgba(255,255,255,0.7)',
                             ...patternStyle(w.pattern, w.color),
                           }}
                           title={`${w.symbol} · ${w.description}`}>
                        {w.symbol}
                      </div>
                    );
                  })}
                </div>

                {/* Edycja warstw */}
                <div className="flex flex-col gap-1.5">
                  {layers.map(w => (
                    <LayerRow key={w.id}
                              layer={w}
                              isEdit={isEdit}
                              busy={busy}
                              lithologyTypes={lithologyTypes}
                              onChange={patch => onUpdateLayer(w.id, patch)}
                              onPickLithology={id => onPickLithology(id, w.id)}
                              onSave={() => onSaveLayer(w)}
                              onDelete={() => onDeleteLayer(w)}/>
                  ))}
                </div>
              </div>
            )}
            <button onClick={onAddLayer} disabled={busy} className="btn btn-secondary btn-sm mt-2">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj warstwę
            </button>
          </section>
        </div>

        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={saveBorehole} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Utwórz otwór i warstwy')}
          </button>
        </div>
      </div>
    </div>
  );
}

function LayerRow({ layer, isEdit, busy, lithologyTypes, onChange, onPickLithology, onSave, onDelete }: {
  layer: BoreholeLayer;
  isEdit: boolean;
  busy: boolean;
  lithologyTypes: LithologyType[];
  onChange: (patch: Partial<BoreholeLayer>) => void;
  onPickLithology: (id: number | null) => void;
  onSave: () => void;
  onDelete: () => void;
}) {
  const isUnsaved = layer.id < 0;
  return (
    <div className="border border-ink-100 rounded p-2 grid grid-cols-[70px_70px_220px_1fr_44px_auto] gap-2 items-center text-[11px]">
      <input type="number" step="0.01" value={layer.fromDepth}
             onChange={e => onChange({ fromDepth: Number(e.target.value) })}
             title="Od (m)" placeholder="od" className="input font-mono"/>
      <input type="number" step="0.01" value={layer.toDepth}
             onChange={e => onChange({ toDepth: Number(e.target.value) })}
             title="Do (m)" placeholder="do" className="input font-mono"/>
      <SearchableSelect
        value={layer.lithologyTypeId == null ? '' : String(layer.lithologyTypeId)}
        onChange={v => onPickLithology(v ? Number(v) : null)}
        className="text-[11px]"
        title="Typ warstwy z katalogu (Administracja → Słowniki opracowań → Typy warstw geol.)"
      >
        <option value="">— typ niestandardowy —</option>
        {lithologyTypes.map(t => (
          <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
        ))}
      </SearchableSelect>
      <input value={layer.description}
             onChange={e => onChange({ description: e.target.value })}
             placeholder="opis dodatkowy (opcjonalnie nadpisuje wybór z katalogu)"
             className="input"/>
      <HatchingSwatch pattern={layer.pattern} color={layer.color}/>
      <div className="flex gap-1">
        {isEdit && (
          <button onClick={onSave} disabled={busy} className="text-water-600 hover:text-water-800 p-1"
                  title={isUnsaved ? 'Zapisz nową warstwę' : 'Zapisz zmiany'}>
            <Icon name="check" className="w-3.5 h-3.5"/>
          </button>
        )}
        <button onClick={onDelete} disabled={busy} className="text-red-500 hover:text-red-700 p-1"
                title="Usuń warstwę">
          <Icon name="x" className="w-3.5 h-3.5"/>
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}

function layerToInput(w: BoreholeLayer): BoreholeLayerInput {
  return {
    fromDepth: w.fromDepth,
    toDepth: w.toDepth,
    symbol: w.symbol || '?',
    description: w.description || w.symbol || 'warstwa',
    color: w.color,
    pattern: w.pattern || 'SOLID',
    lithologyTypeId: w.lithologyTypeId ?? undefined,
  };
}
